import React, { useEffect, useRef, useState } from "react";
import { getNotifications } from "../lib/api";
import { Bell, AlertTriangle, ShieldCheck, KeyRound, Siren, ShieldAlert, X } from "lucide-react";

const ICONS = { certificate: ShieldAlert, license: KeyRound, e911: Siren, gate: ShieldCheck };
const SEV = {
  critical: "text-red-400 bg-red-500/10 border-red-500/20",
  warning: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  info: "text-blue-400 bg-blue-500/10 border-blue-500/20",
};

export const NotificationBell = () => {
  const [data, setData] = useState({ count: 0, counts: {}, alerts: [] });
  const [open, setOpen] = useState(false);
  const seenRef = useRef(new Set(JSON.parse(localStorage.getItem("lvai_seen_alerts") || "[]")));

  const poll = async () => {
    try {
      const d = await getNotifications();
      setData(d);
      // Fire a browser notification for any new critical/warning alert.
      if (Notification?.permission === "granted") {
        const fresh = d.alerts.filter((a) => a.severity !== "info" && !seenRef.current.has(a.id));
        fresh.slice(0, 3).forEach((a) => {
          try { new Notification(a.title, { body: a.message, icon: "/icon-192.png", tag: a.id }); } catch (_) {}
        });
      }
      d.alerts.forEach((a) => seenRef.current.add(a.id));
      localStorage.setItem("lvai_seen_alerts", JSON.stringify([...seenRef.current].slice(-200)));
    } catch (_) {}
  };

  useEffect(() => {
    poll();
    const t = setInterval(poll, 120000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const enableNotifs = () => {
    if (Notification?.permission === "default") Notification.requestPermission().then(poll);
  };

  const badge = data.counts?.critical || data.counts?.warning ? "bg-red-500" : data.count ? "bg-blue-500" : "";

  return (
    <div className="relative">
      <button
        onClick={() => { setOpen((o) => !o); enableNotifs(); }}
        data-testid="notification-bell"
        className="relative h-9 w-9 flex items-center justify-center rounded-md border border-zinc-800 text-zinc-300 hover:bg-zinc-800 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {data.count > 0 && (
          <span data-testid="notification-count" className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full ${badge} text-white text-[10px] font-bold flex items-center justify-center`}>
            {data.count > 99 ? "99+" : data.count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 shadow-2xl z-50 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
              <span className="text-sm font-bold text-white">Notifications</span>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" data-testid="notification-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="overflow-y-auto">
              {data.alerts.length === 0 ? (
                <div className="px-4 py-8 text-center text-[13px] text-zinc-500">
                  You're all clear — no expiring certs, licenses or compliance gaps.
                </div>
              ) : (
                data.alerts.map((a) => {
                  const Icon = ICONS[a.type] || AlertTriangle;
                  return (
                    <div key={a.id} data-testid={`notification-${a.id}`} className="flex gap-3 px-4 py-3 border-b border-zinc-900 hover:bg-zinc-900/50">
                      <span className={`h-7 w-7 rounded-md border flex items-center justify-center shrink-0 ${SEV[a.severity]}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0">
                        <div className="text-[13px] text-zinc-200 font-medium leading-snug">{a.title}</div>
                        <div className="text-[12px] text-zinc-500 leading-snug mt-0.5">{a.message}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {Notification?.permission !== "granted" && (
              <button onClick={enableNotifs} data-testid="enable-notifications-btn" className="text-[12px] text-blue-400 hover:text-blue-300 px-4 py-2.5 border-t border-zinc-800 text-left">
                Enable device notifications →
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
